import { createSelector } from "@reduxjs/toolkit";
import { LoyaltyItemOffer } from "../../../utils/LoyaltyOfferClass/LoyaltyItemOffer";
import LoyaltyOffer from "../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import { createLoyaltyOffer } from "../../../utils/LoyaltyOfferClass/LoyaltyOfferCreator/loyaltyOfferCreator";
import { isSupportedGuestBuiltOffer } from "../../../utils/loyalty/offerSelection";
import {
  LoyaltyChannel,
  LoyaltyOfferSystem,
  LoyaltyOrderType,
} from "../../../utils/loyaltyTypes";
import type { RootState } from "../app/store";
import {
  selectCustomerLoyaltyOffersRaw,
  selectLoyaltyCartResult,
  selectRedeemedLoyaltyOfferRaw,
} from "./loyaltyCartSelectors";
import { selectUseLoyaltyService } from "./loyaltyServiceSelectors";

export const selectCustomerLoyaltyOffers = createSelector(
  selectCustomerLoyaltyOffersRaw,
  (rawOffers) =>
    rawOffers
      ?.map(createLoyaltyOffer)
      .filter(
        (offer, index, self) =>
          index === self.findIndex((t) => t.id === offer.id)
      )
);

/** UI config source: server `appliedOffer.offerId` → customer catalog, else active redemption. */
export const selectResolvedAppliedOffer = createSelector(
  selectLoyaltyCartResult,
  selectCustomerLoyaltyOffersRaw,
  selectRedeemedLoyaltyOfferRaw,
  (result, customerOffers, redeemedRaw) => {
    const responseOfferId = result?.appliedOffer?.offerId;
    if (responseOfferId) {
      const fromCatalog = customerOffers?.find((o) => o.id === responseOfferId);
      if (fromCatalog) {
        return createLoyaltyOffer(fromCatalog);
      }
    }

    if (redeemedRaw) {
      const raw =
        customerOffers?.find((o) => o.id === redeemedRaw.id) ?? redeemedRaw;

      return createLoyaltyOffer(raw);
    }

    return undefined;
  }
);

export const selectRedeemedLoyaltyOffer = createSelector(
  selectRedeemedLoyaltyOfferRaw,
  selectCustomerLoyaltyOffersRaw,
  (redeemedRaw, allOffers): LoyaltyOffer | undefined => {
    if (!redeemedRaw) {
      return undefined;
    }

    const raw = allOffers?.find((o) => o.id === redeemedRaw.id);

    return raw ? createLoyaltyOffer(raw) : undefined;
  }
);

export const selectSortedCustomerLoyaltyOffers = createSelector(
  selectCustomerLoyaltyOffers,
  selectRedeemedLoyaltyOffer,
  (offers, redeemed) => {
    if (!offers?.length || !redeemed) {
      return offers;
    }

    const redeemedIndex = offers.findIndex((offer) => offer.isEqual(redeemed));
    if (redeemedIndex <= 0) {
      return offers;
    }

    const sortedOffers = [...offers];
    sortedOffers.unshift(sortedOffers.splice(redeemedIndex, 1)[0]);

    return sortedOffers;
  }
);

/**
 * A category-target BOGO or bundle cannot be built, so it stays hidden rather
 * than reaching a surface whose only action is to open a page it cannot draw.
 */
function isDisplayableOffer(
  offer: LoyaltyOffer,
  useLoyaltyService: boolean
): boolean {
  return (
    !useLoyaltyService ||
    !(offer.isBogoOffer() || offer.isBundleOffer()) ||
    (offer instanceof LoyaltyItemOffer && isSupportedGuestBuiltOffer(offer))
  );
}

export const selectLoyaltyFlashOffers = createSelector(
  selectSortedCustomerLoyaltyOffers,
  selectUseLoyaltyService,
  (offers, useLoyaltyService) =>
    offers?.filter(
      (offer) =>
        offer.offerSystem !== LoyaltyOfferSystem.MILESTONE &&
        isDisplayableOffer(offer, useLoyaltyService)
    )
);

export const selectApplicableLoyaltyOffers = createSelector(
  [
    selectSortedCustomerLoyaltyOffers,
    (_state: RootState, context: { timeZone: string }) => context.timeZone,
    (
      _state: RootState,
      context: { mappedOrderType?: LoyaltyOrderType | null }
    ) => context.mappedOrderType,
    (_state: RootState, context: { channel?: LoyaltyChannel }) =>
      context.channel,
    (state: RootState) => state.config.entityImageLookup,
    (state: RootState) => state.itemStatus,
    selectUseLoyaltyService,
  ],
  (
    offers,
    timeZone,
    mappedOrderType,
    channel,
    entityImageLookup,
    itemStatusRedux,
    useLoyaltyService
  ) =>
    (offers ?? []).filter(
      (offer) =>
        isDisplayableOffer(offer, useLoyaltyService) &&
        offer.isOfferApplicable({
          timeZone,
          orderType: mappedOrderType ?? undefined,
          channel,
          entityImageLookup,
          itemStatusRedux,
        }).applicable
    )
);

/**
 * BOGO and bundle offers the guest builds on the offer page. ITEMS targets
 * only — CATEGORIES offers are hidden rather than shown broken. Loyalty-service
 * path only; the legacy experience is untouched.
 */
export const selectGuestBuiltLoyaltyOffers = createSelector(
  [
    selectSortedCustomerLoyaltyOffers,
    (_state: RootState, context: { timeZone: string }) => context.timeZone,
    (
      _state: RootState,
      context: { mappedOrderType?: LoyaltyOrderType | null }
    ) => context.mappedOrderType,
    (_state: RootState, context: { channel?: LoyaltyChannel }) =>
      context.channel,
    (state: RootState) => state.config.entityImageLookup,
    (state: RootState) => state.itemStatus,
    selectUseLoyaltyService,
  ],
  (
    offers,
    timeZone,
    mappedOrderType,
    channel,
    entityImageLookup,
    itemStatusRedux,
    useLoyaltyService
  ): LoyaltyItemOffer[] => {
    if (!useLoyaltyService) {
      return [];
    }

    return (offers ?? []).filter(
      (offer): offer is LoyaltyItemOffer =>
        offer instanceof LoyaltyItemOffer &&
        isSupportedGuestBuiltOffer(offer) &&
        offer.isOfferApplicable({
          timeZone,
          orderType: mappedOrderType ?? undefined,
          channel,
          entityImageLookup,
          itemStatusRedux,
        }).applicable
    );
  }
);
